import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/pensionCalc';
import ConfirmModal from '../components/ConfirmModal';
import Toast from '../components/Toast';
import StatusPill from '../components/StatusPill';
import ProgressBar from '../components/ProgressBar';
import SummaryTable, { deriveFlags } from '../components/SummaryTable';
import ReconciliationFooter from '../components/ReconciliationFooter';
import FlagFilterChips from '../components/FlagFilterChips';
import AdminAdjustmentEditor from '../components/AdminAdjustmentEditor';
import AdminAmendmentList from '../components/AdminAmendmentList';
import { DIRECTOR_NI_LABELS, MONTH_NAMES, personalDetailsSummary } from '../utils/constants';
import PayrollNotes from '../components/PayrollNotes';

// ── Copy-to-clipboard field with checkbox ────────────────────────────────────

function CopyField({ label, value, copyValue, warn }) {
  const [copied, setCopied] = useState(false);
  if (!value && value !== 0) return null;
  const display = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
  // What actually lands on the clipboard. Defaults to the displayed text, but
  // callers can override it — e.g. money fields copy the bare number so they
  // paste straight into BrightPay without the £ sign or thousands separators.
  const toCopy = copyValue !== undefined && copyValue !== null ? String(copyValue) : display;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(toCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className={`flex items-center justify-between py-1.5 px-3 rounded group ${
      warn ? 'bg-amber-50 ring-1 ring-amber-200 hover:bg-amber-100' : 'hover:bg-gray-50'
    }`}>
      <div className="min-w-0">
        <span className={`text-xs block ${warn ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
          {warn && <span aria-hidden="true" className="mr-1">⚠</span>}{label}
        </span>
        <span className={`text-sm font-medium ${warn ? 'text-amber-900' : 'text-ink'}`}>{display}</span>
      </div>
      <button
        onClick={handleCopy}
        className={`ml-2 flex-shrink-0 text-xs px-2 py-1 rounded transition-colors ${
          copied ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500 opacity-0 group-hover:opacity-100'
        }`}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

// ── Type metadata ────────────────────────────────────────────────────────────

const TYPE_LABELS = {
  new_starter: 'New Starters', leaver: 'Leavers', salary: 'Salary Changes',
  pension: 'Pension Updates', parental_leave: 'Parental Leave', sick_leave: 'Sick Leave',
  unpaid_leave: 'Unpaid Leave',
  addition: 'Additions', deduction: 'Deductions', bank_details: 'Bank Detail Changes',
  address_change: 'Address Changes', contractual_change: 'Contractual Changes',
  personal_details: 'Personal Details',
};

const TYPE_ACCENT = {
  new_starter: 'emerald', leaver: 'rose', salary: 'amber', pension: 'sky',
  parental_leave: 'sky', sick_leave: 'amber', unpaid_leave: 'amber',
  addition: 'emerald', deduction: 'amber', bank_details: 'sky',
  address_change: 'sky', contractual_change: 'emerald', personal_details: 'sky',
};

const TYPE_ORDER = [
  'new_starter', 'leaver', 'salary', 'pension', 'parental_leave',
  'sick_leave', 'unpaid_leave', 'addition', 'deduction', 'bank_details',
  'address_change', 'personal_details', 'contractual_change',
];

const PRORATA_METHODS = [
  { value: 'working_260', label: 'Working days (annual ÷ 260)', description: 'Most common UK convention. Mon–Fri, includes bank holidays as paid days.' },
  { value: 'calendar_365', label: 'Calendar days (annual ÷ 365)', description: 'Pay accrues every calendar day. Used where contracts say "per calendar day".' },
  { value: 'month_working', label: 'Month-specific working days', description: 'Monthly pay × (working days worked / working days in this calendar month). Most accurate per HMRC but uneven monthly figures.' },
  { value: 'custom', label: 'Custom divisor', description: 'Use a contractual divisor (e.g. 252, 261). Daily rate = annual ÷ divisor.' },
];

function shortDate(val) {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// One-line summary for the collapsed Action List card.
function getOneLineSummary(change) {
  const d = change.data || {};
  switch (change.type) {
    case 'new_starter':
      return [d.start_date && `start ${formatDateUK(d.start_date)}`,
              d.job_title,
              d.annual_salary && `£${Number(d.annual_salary).toLocaleString('en-GB')}/yr`,
              d.pay_type === 'hourly' && d.hourly_rate && `£${Number(d.hourly_rate).toFixed(2)}/hr`].filter(Boolean).join(' · ');
    case 'leaver':
      return [d.leaving_date && `last day ${formatDateUK(d.leaving_date)}`, d.reason].filter(Boolean).join(' · ');
    case 'salary':
      return [d.effective_date && `eff ${formatDateUK(d.effective_date)}`,
              d.new_annual_salary && `£${Number(d.new_annual_salary).toLocaleString('en-GB')}/yr`,
              d.new_hourly_rate && `£${Number(d.new_hourly_rate).toFixed(2)}/hr`].filter(Boolean).join(' · ');
    case 'pension':
      return [d.action || d.pension_action,
              d.employer_value !== undefined && `ER ${d.employer_value}${d.employer_type === 'percentage' ? '%' : ''}`,
              d.employee_value !== undefined && `EE ${d.employee_value}${d.employee_type === 'percentage' ? '%' : ''}`].filter(Boolean).join(' · ');
    case 'parental_leave':
      return [d.leave_type, d.start_date && `from ${formatDateUK(d.start_date)}`].filter(Boolean).join(' · ');
    case 'sick_leave': {
      const days = Array.isArray(d.actual_sick_days) ? d.actual_sick_days.length : null;
      return [d.start_date && `from ${formatDateUK(d.start_date)}`,
              days !== null && `${days} day${days === 1 ? '' : 's'}`,
              d.return_date && `returned ${formatDateUK(d.return_date)}`].filter(Boolean).join(' · ');
    }
    case 'bank_details':
      return [d.sort_code && `sort ${d.sort_code}`, d.account_number && `acct ${d.account_number}`].filter(Boolean).join(' · ');
    case 'address_change':
      return [d.city, d.postcode].filter(Boolean).join(' · ');
    case 'personal_details':
      return personalDetailsSummary(d);
    case 'contractual_change':
      return [d.change_type_label || d.change_type,
              d.new_job_title && `to "${d.new_job_title}"`,
              d.effective_date && `eff ${formatDateUK(d.effective_date)}`,
              d.new_annual_salary && `£${Number(d.new_annual_salary).toLocaleString('en-GB')}/yr`].filter(Boolean).join(' · ');
    default:
      return '';
  }
}

// The working pattern as the starter form saved it. Kept as its own row because
// Part-Time Basis only describes the hours when the client left their notes blank.
function starterPattern(d) {
  const days = parseFloat(d.pt_days_per_week) || 0;
  const perDay = parseFloat(d.pt_hours_per_day) || 0;
  if (days > 0 && perDay > 0) {
    return { text: `${days} days × ${perDay} hrs (${(days * perDay).toFixed(1)} hrs/week)`, fraction: days <= 5 ? days / 5 : 0 };
  }
  const hours = parseFloat(d.pt_weekly_hours_custom ?? d.pt_weekly_hours) || 0;
  const fte = parseFloat(d.pt_fte_weekly_hours) || 0;
  if (hours > 0 && fte > 0) {
    return { text: `${hours} of ${fte} hrs/week (${Math.round(hours / fte * 100)}% of full-time)`, fraction: hours < fte ? hours / fte : 0 };
  }
  return null;
}

// Payroll pays a part-timer from the annual (full-time) salary scaled by their
// hours. If the client's pro-rata figure doesn't match that, one of the two
// salaries is probably not what they think it is — typically the real salary
// typed into both boxes.
function proRataMismatch(d, pattern) {
  const annual = parseFloat(d.annual_salary) || 0;
  const proRata = parseFloat(d.prorata_salary) || 0;
  if (d.employment_type !== 'part-time' || d.pay_type === 'hourly' || !pattern?.fraction || !annual || !proRata) return null;
  const expected = annual * pattern.fraction;
  if (Math.abs(expected - proRata) <= 1) return null;
  return `Annual salary ${fmtMoney(annual)} at these hours is ${fmtMoney(expected)}, but the client entered ${fmtMoney(proRata)} as pro-rata. `
    + `Payroll will pay ${fmtMoney(expected)} a year. Check with the client which is their actual pay.`;
}

function getFields(change) {
  const d = change.data || {};
  switch (change.type) {
    case 'new_starter': {
      const employerType = d.pension_employer_type || d.employer_contribution_type;
      const employerValue = d.pension_employer_value || d.employer_contribution_value;
      const employeeType = d.pension_employee_type || d.employee_contribution_type;
      const employeeValue = d.pension_employee_value || d.employee_contribution_value;
      // The starter form seeds deferral_months to its default (3) and never clears it
      // when the client switches to immediate enrolment, so older records carry a
      // stale value. Only a deferring starter actually has a deferral period.
      const pensionEnroll = d.pension_enroll || d.pension_enrollment;
      const pattern = d.employment_type === 'part-time' ? starterPattern(d) : null;
      return [
        { label: 'First Name', value: d.first_name }, { label: 'Last Name', value: d.last_name },
        { label: 'Email', value: d.email }, { label: 'Gender', value: d.gender },
        { label: 'Date of Birth', value: formatDateUK(d.date_of_birth) }, { label: 'NI Number', value: d.ni_number },
        { label: 'Address Line 1', value: d.address_line_1 || d.address },
        { label: 'Address Line 2', value: d.address_line_2 },
        { label: 'City', value: d.city }, { label: 'Postcode', value: d.postcode },
        { label: 'Start Date', value: formatDateUK(d.start_date) }, { label: 'Job Title', value: d.job_title },
        { label: 'Contract Type', value: fmtEnum(d.contract_type, { 'permanent': 'Permanent', 'fixed-term': 'Fixed Term', 'part-time': 'Part-Time' }) },
        { label: 'Contract End Date', value: formatDateUK(d.contract_end_date) },
        { label: 'Employment Type', value: fmtEnum(d.employment_type, { 'full-time': 'Full-Time', 'part-time': 'Part-Time' }) },
        { label: 'Working Pattern', value: pattern?.text },
        { label: 'Part-Time Basis', value: d.part_time_basis },
        { label: 'Pro-Rata Salary', value: fmtMoney(d.prorata_salary) },
        { label: 'Salaries don\'t match the hours', value: proRataMismatch(d, pattern), warn: true },
        { label: 'Pro-Rata Method (client picked)', value: d.prorata_method ? (PRORATA_METHODS.find(m => m.value === d.prorata_method)?.label || d.prorata_method) : null },
        { label: 'Pro-Rata Custom Divisor', value: d.prorata_method === 'custom' && d.prorata_custom_divisor ? d.prorata_custom_divisor : null },
        { label: 'Is Director', value: fmtBool(d.is_director) },
        { label: 'Director NI Method', value: d.is_director ? (DIRECTOR_NI_LABELS[d.director_ni_method] || null) : null },
        { label: 'Appointed Director', value: d.is_director ? formatDateUK(d.director_appointed_date) : null },
        { label: 'Pay Type', value: d.pay_type === 'hourly' ? 'Hourly' : (d.pay_type === 'salaried' ? 'Salaried' : null) },
        { label: 'Hourly Rate', value: d.hourly_rate ? fmtMoney(d.hourly_rate) + '/hr' : null },
        { label: 'Contracted Hours/Week', value: d.contracted_hours_per_week },
        { label: 'Annual Salary', value: fmtMoney(d.annual_salary), copyValue: d.annual_salary != null && d.annual_salary !== '' ? Number(d.annual_salary) : undefined },
        { label: 'Pay Frequency', value: fmtEnum(d.pay_frequency, { 'monthly': 'Monthly', 'weekly': 'Weekly', '4-weekly': '4-Weekly' }) },
        { label: 'Student Loan', value: d.student_loan && d.student_loan !== 'none' ? d.student_loan : null },
        { label: 'Tax Code', value: d.tax_code },
        { label: 'Bank Account Name', value: d.bank_account_name }, { label: 'Sort Code', value: d.sort_code },
        { label: 'Account Number', value: d.account_number }, { label: 'Roll Number', value: d.roll_number },
        { label: 'Pension Enrollment', value: fmtEnum(pensionEnroll, { 'immediate': 'Enrol immediately', 'defer': 'Defer assessment', 'opt_out': 'Opt out' }) },
        { label: 'Deferral Period', value: pensionEnroll === 'defer' && d.deferral_months ? `${d.deferral_months} month${d.deferral_months === 1 ? '' : 's'}` : null },
        // Stamped on by the backend when the client's choice contradicted the
        // company's recorded postponement policy. Flagged rather than blocked —
        // the reason is the client's own words, so it can be taken at face value
        // or queried, but it can't go unnoticed.
        { label: 'Against company pension policy', value: d.pension_policy_deviation
          ? `Policy is to ${d.pension_policy_deviation.expected_label}, submitted as ${d.pension_policy_deviation.chosen_label}`
          : null, warn: true },
        { label: 'Reason given', value: d.pension_policy_deviation?.reason || null, warn: true },
        { label: 'Earnings Basis', value: fmtEnum(d.pension_earnings_basis || d.earnings_basis, { 'qualifying': 'Qualifying earnings', 'full': 'Full earnings' }) },
        { label: 'Employer Contribution', value: fmtPct(employerType, employerValue) },
        { label: 'Employee Contribution', value: fmtPct(employeeType, employeeValue) },
        { label: 'Notes', value: d.notes },
      ];
    }
    case 'leaver':
      return [
        { label: 'Employee', value: d.employee_name }, { label: 'Leaving Date', value: formatDateUK(d.leaving_date || d.leave_date) },
        { label: 'Reason', value: d.reason },
        { label: d.holiday_balance_direction === 'owed_to_company' ? 'Holiday Days Overtaken' : 'Holiday Days Owed', value: d.holiday_days_owed },
        {
          label: d.holiday_balance_direction === 'owed_to_company' ? 'Holiday Pay to Deduct' : 'Holiday Pay Owed',
          value: d.holiday_pay_owed
            ? (d.holiday_balance_direction === 'owed_to_company' ? `−${fmtMoney(d.holiday_pay_owed)}` : fmtMoney(d.holiday_pay_owed))
            : null,
        },
        { label: 'Final Hours Worked', value: d.final_hours_worked ? `${d.final_hours_worked} hrs` : null },
        { label: 'Final Payment Adjustments', value: d.final_payment_adjustments },
        { label: 'P45 Auto-Issued', value: d.p45_auto_issued }, { label: 'Notes', value: d.notes },
      ];
    case 'salary':
      // Part-time salary changes must lead with the pro-rata figure — that is the number
      // the processor keys into BrightPay. Showing only the FTE (as this used to) means
      // keying a salary the employee is not paid. The FTE is still shown, labelled, so
      // both figures are visible and it is obvious which is which.
      if (d.is_part_time && d.new_prorata_salary) {
        return [
          { label: 'Employee', value: d.employee_name },
          {
            label: 'Pay this — pro-rata',
            value: `${fmtMoney(d.previous_prorata_salary)} → ${fmtMoney(d.new_prorata_salary)}`,
            copyValue: Number(d.new_prorata_salary),
          },
          {
            label: 'Pro-rata monthly',
            value: d.new_monthly_salary
              ? `${fmtMoney(Number(d.previous_prorata_salary) / 12)} → ${fmtMoney(d.new_monthly_salary)}`
              : null,
            copyValue: d.new_monthly_salary ? Number(d.new_monthly_salary) : undefined,
          },
          { label: 'Pattern', value: d.pt_pattern },
          {
            label: 'Full-time equivalent',
            value: `${fmtMoney(d.previous_fte_salary || d.previous_salary)} → ${fmtMoney(d.new_fte_salary || d.new_annual_salary)}`,
          },
          { label: 'Effective Date', value: formatDateUK(d.effective_date) },
          { label: 'Notes', value: d.notes },
        ];
      }
      return [
        { label: 'Employee', value: d.employee_name },
        { label: 'New Hourly Rate', value: d.new_hourly_rate ? fmtMoney(d.new_hourly_rate) + '/hr' : null },
        { label: 'Previous Hourly Rate', value: d.previous_hourly_rate ? fmtMoney(d.previous_hourly_rate) + '/hr' : null },
        { label: 'New Salary', value: fmtMoney(d.new_salary || d.new_annual_salary) },
        { label: 'Previous Salary', value: fmtMoney(d.previous_salary) },
        { label: 'Effective Date', value: formatDateUK(d.effective_date) },
        { label: 'Notes', value: d.notes },
      ];
    case 'pension': {
      // Show a before → after when we have the previous state captured at submit
      // time. A client can flip a pension status (e.g. opted-out → enrolled) with
      // no other signal to the admin, so surfacing the transition here is the
      // review's only chance to catch it. Falls back to just the new value when
      // there's no previous state (older submissions predate this) or nothing
      // changed for that field.
      const prev = change.previous_state || null;
      const diff = (prevVal, newVal) => {
        if (newVal === undefined || newVal === null || newVal === '') return null;
        const changed = prev && String(prevVal ?? '') !== String(newVal ?? '');
        return changed ? `${prevVal ?? '—'} → ${newVal}` : newVal;
      };
      return [
        { label: 'Employee', value: d.employee_name },
        { label: 'Status', value: diff(prev?.status, d.status) },
        { label: 'Earnings Basis', value: diff(prev?.earnings_basis, d.earnings_basis) },
        { label: 'Employer Type', value: diff(prev?.employer_type, d.employer_type) },
        { label: 'Employer Value', value: diff(prev?.employer_value, d.employer_value) },
        { label: 'Employee Type', value: diff(prev?.employee_type, d.employee_type) },
        { label: 'Employee Value', value: diff(prev?.employee_value, d.employee_value) },
        { label: 'Notes', value: d.notes },
      ];
    }
    case 'parental_leave':
      return [
        { label: 'Employee', value: d.employee_name }, { label: 'Leave Type', value: d.leave_type },
        { label: 'Expected Birth Date', value: formatDateUK(d.expected_birth_date) },
        { label: 'Desired Leave Start', value: formatDateUK(d.desired_leave_start_date || d.start_date) },
        { label: 'Expected Return', value: formatDateUK(d.expected_return_date) }, { label: 'Pay Type', value: d.pay_type },
        { label: 'Notes', value: d.notes },
      ];
    case 'sick_leave':
      return [
        { label: 'Employee', value: d.employee_name }, { label: 'Start Date', value: formatDateUK(d.start_date) },
        { label: 'Expected End Date', value: formatDateUK(d.expected_end_date) },
        { label: 'Reason', value: d.reason }, { label: 'Fit Note Received', value: d.fit_note_received },
        { label: 'Notes', value: d.notes },
      ];
    case 'unpaid_leave':
      return [
        { label: 'Employee', value: d.employee_name },
        { label: 'Start Date', value: formatDateUK(d.start_date) },
        { label: 'End Date', value: formatDateUK(d.end_date) },
        { label: 'Reason', value: d.reason_label || d.reason },
        { label: 'Notes', value: d.notes },
      ];
    case 'addition':
      return [
        { label: 'Employee', value: d.employee_name }, { label: 'Type', value: d.addition_type_label || d.addition_type },
        { label: 'Amount', value: fmtMoney(d.amount) }, { label: 'Number of Days', value: d.number_of_days },
        { label: 'Selected Dates', value: Array.isArray(d.selected_dates) ? d.selected_dates.map(formatDateUK).join(', ') : d.selected_dates },
        { label: 'Effective Date', value: formatDateUK(d.effective_date) }, { label: 'Recurring', value: d.recurring },
        { label: 'Notes', value: d.notes },
      ];
    case 'deduction':
      return [
        { label: 'Employee', value: d.employee_name }, { label: 'Type', value: d.deduction_type_label || d.deduction_type },
        { label: 'Deduct From', value: d.deduct_from === 'pre_tax' ? 'Pre-tax (before tax & NI)' : d.deduct_from === 'net' ? 'Net pay (after tax & NI)' : null },
        { label: 'Total Amount', value: fmtMoney(d.total_amount) }, { label: 'Monthly Deduction', value: fmtMoney(d.monthly_deduction || d.amount) },
        { label: 'Months', value: Array.isArray(d.deduction_months_labels) ? d.deduction_months_labels.join(', ') : d.deduction_months_labels },
        { label: 'Apply In', value: d.apply_month_label },
        { label: 'Effective Date', value: formatDateUK(d.effective_date) }, { label: 'Recurring', value: d.recurring },
        { label: 'Notes', value: d.notes },
      ];
    case 'bank_details':
      return [
        { label: 'Employee', value: d.employee_name }, { label: 'Account Name', value: d.bank_account_name },
        { label: 'Sort Code', value: d.sort_code }, { label: 'Account Number', value: d.account_number },
        { label: 'Notes', value: d.notes },
      ];
    case 'address_change':
      return [
        { label: 'Employee', value: d.employee_name }, { label: 'Address', value: d.address },
        { label: 'Address Line 2', value: d.address_line_2 }, { label: 'City', value: d.city },
        { label: 'Postcode', value: d.postcode }, { label: 'Notes', value: d.notes },
      ];
    case 'personal_details':
      return [
        { label: 'Employee', value: d.employee_name },
        { label: 'Previous First Name', value: d.new_first_name ? d.previous_first_name : null },
        { label: 'New First Name', value: d.new_first_name },
        { label: 'Previous Last Name', value: d.new_last_name ? d.previous_last_name : null },
        { label: 'New Last Name', value: d.new_last_name },
        { label: 'Previous Date of Birth', value: d.new_date_of_birth ? formatDateUK(d.previous_date_of_birth) : null },
        { label: 'New Date of Birth', value: formatDateUK(d.new_date_of_birth) },
        { label: 'Previous Gender', value: d.new_gender ? d.previous_gender : null },
        { label: 'New Gender', value: d.new_gender },
        { label: 'New NI Number', value: d.new_ni_number },
        { label: 'Reason', value: d.reason }, { label: 'Notes', value: d.notes },
      ];
    case 'contractual_change':
      return [
        { label: 'Employee', value: d.employee_name }, { label: 'Change Type', value: d.change_type_label || d.change_type },
        { label: 'Previous Job Title', value: d.previous_job_title },
        { label: 'New Job Title', value: d.new_job_title },
        { label: 'New Pay Type', value: d.new_pay_type === 'hourly' ? 'Hourly' : d.new_pay_type === 'salaried' ? 'Salaried' : null },
        { label: 'New Hourly Rate', value: d.new_hourly_rate ? fmtMoney(d.new_hourly_rate) + '/hr' : null },
        { label: 'Contracted Hours/Week', value: d.new_contracted_hours },
        { label: 'New Annual Salary', value: fmtMoney(d.new_annual_salary) },
        { label: 'Pro-Rata Salary', value: fmtMoney(d.pro_rata_salary) },
        { label: 'New Weekly Hours', value: d.new_weekly_hours },
        { label: 'Effective Date', value: formatDateUK(d.effective_date) }, { label: 'Notes', value: d.notes },
      ];
    default:
      return Object.entries(d).map(([k, v]) => ({ label: k.replace(/_/g, ' '), value: v }));
  }
}

function formatDateUK(val) {
  if (!val) return val;
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('en-GB');
}

function fmtMoney(val) {
  if (val === undefined || val === null || val === '') return val;
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  return formatCurrency(num);
}

function fmtBool(val) {
  if (val === true || val === 'true' || val === 'yes') return 'Yes';
  if (val === false || val === 'false' || val === 'no') return 'No';
  return val;
}

function fmtEnum(val, map) {
  if (val === undefined || val === null || val === '') return val;
  return map[val] || val;
}

function fmtPct(type, val) {
  if (val === undefined || val === null || val === '') return val;
  if (type === 'percentage') return `${val}%`;
  if (type === 'fixed') return fmtMoney(val);
  return val;
}

// ── Right-rail building blocks ──────────────────────────────────────────────

function SectionLabel({ children, count, accent = 'brand' }) {
  const bg = { brand: 'bg-brand-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500', rose: 'bg-rose-500', sky: 'bg-sky-500', violet: 'bg-violet-500' }[accent] || 'bg-brand-500';
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className={`w-1 h-5 ${bg} rounded-full`} aria-hidden="true" />
      <h3 className="text-[15px] font-bold tracking-[0.12em] uppercase text-ink">{children}</h3>
      {count != null && <span className="text-gray-400 text-[15px] tabular">({count})</span>}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

const DENSITY_KEY = 'payroll.summary.density';

export default function ProcessingViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Standard users are the payroll team: they process submissions, so every
  // processing control is theirs too. isAdmin is for admin-only settings.
  const isTeam = isAdmin || user?.role === 'standard';

  const [submission, setSubmission] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [payrollNotes, setPayrollNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [groupByEmployee, setGroupByEmployee] = useState(true);
  const [proRataMethod, setProRataMethod] = useState('working_260');
  const [customDivisor, setCustomDivisor] = useState('260');
  const [runSheet, setRunSheet] = useState(null);
  const [runSheetLoading, setRunSheetLoading] = useState(false);
  const [runSheetError, setRunSheetError] = useState('');
  const [expandedChangeKeys, setExpandedChangeKeys] = useState(() => new Set());
  const [excludedCarryover, setExcludedCarryover] = useState(() => new Set());

  const [density, setDensity] = useState(() => {
    if (typeof window === 'undefined') return 'cozy';
    return window.localStorage.getItem(DENSITY_KEY) || 'cozy';
  });
  const [flagFilter, setFlagFilter] = useState(() => new Set());
  const [expandSignal, setExpandSignal] = useState(0);
  const [collapseSignal, setCollapseSignal] = useState(0);

  const [queue, setQueue] = useState([]);
  const [queueFilter, setQueueFilter] = useState('all'); // 'all' | 'open' | 'done'
  const [queueSearch, setQueueSearch] = useState('');
  const [queueCollapsed, setQueueCollapsed] = useState(() => {
    try { return localStorage.getItem('processingQueueCollapsed') === '1'; } catch { return false; }
  });
  const toggleQueueCollapsed = () => {
    setQueueCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('processingQueueCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };
  const [activity, setActivity] = useState([]);

  const saveTimer = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(DENSITY_KEY, density);
  }, [density]);

  const loadNotes = useCallback(async () => {
    try {
      const res = await api.get(`/admin/submissions/${id}/notes`);
      setNotes(res.data || []);
    } catch (err) { /* notes table may not exist yet */ }
  }, [id]);

  const loadActivity = useCallback(async () => {
    try {
      const res = await api.get(`/admin/submissions/${id}/activity`);
      setActivity(res.data || []);
    } catch (err) {
      setActivity([]);
    }
  }, [id]);

  const handleAmendmentSourceSaved = (amendmentId, source) => {
    setRunSheet(prev => {
      if (!prev) return prev;
      const patch = (list) => (list || []).map(a => (a.id === amendmentId ? { ...a, amendment_source: source } : a));
      return {
        ...prev,
        admin_amendments: patch(prev.admin_amendments),
        rows: (prev.rows || []).map(r => (
          r.admin_amendments && r.admin_amendments.length
            ? { ...r, admin_amendments: patch(r.admin_amendments) }
            : r
        )),
      };
    });
  };

  const loadRunSheet = useCallback(async (method = proRataMethod, divisor = customDivisor, excluded = excludedCarryover) => {
    setRunSheetLoading(true);
    setRunSheetError('');
    try {
      const params = new URLSearchParams({ method });
      if (method === 'custom' && divisor) params.set('divisor', divisor);
      if (excluded && excluded.size > 0) {
        params.set('exclude_carryover', Array.from(excluded).join(','));
      }
      const res = await api.get(`/admin/submissions/${id}/run-sheet?${params.toString()}`);
      setRunSheet(res.data);
    } catch (err) {
      setRunSheetError(err.response?.data?.error || err.message);
      setRunSheet(null);
    } finally {
      setRunSheetLoading(false);
    }
  }, [id, proRataMethod, customDivisor, excludedCarryover]);

  const loadQueue = useCallback(async (sub) => {
    if (!sub || !sub.submitted_at) return;
    const submittedDate = new Date(sub.submitted_at);
    const month = submittedDate.getMonth() + 1;
    const year = submittedDate.getFullYear();
    try {
      const res = await api.get('/admin/submissions', { params: { month, year, limit: 100 } });
      const list = res.data.submissions || res.data || [];
      setQueue(Array.isArray(list) ? list : []);
    } catch (err) {
      setQueue([]);
    }
  }, []);

  const toggleCarryover = (employeeId) => {
    setExcludedCarryover(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      loadRunSheet(proRataMethod, customDivisor, next);
      return next;
    });
  };

  const setHolidayDays = async (employeeId, value) => {
    const trimmed = (value || '').toString().trim();
    const numeric = parseFloat(trimmed);
    const adj = { ...(checklist.holiday_adjustments || {}) };
    if (trimmed === '' || !Number.isFinite(numeric) || numeric < 0) {
      delete adj[employeeId];
    } else {
      adj[employeeId] = { ...(adj[employeeId] || {}), days: trimmed };
    }
    const nextChecklist = { ...checklist, holiday_adjustments: adj };
    setChecklist(nextChecklist);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    try {
      await api.patch(`/admin/submissions/${id}/checklist`, { checklist: nextChecklist });
      await loadRunSheet();
    } catch (err) {
      console.error('Failed to save holiday adjustment:', err);
    }
  };

  // Persist a per-employee admin adjustment list onto the checklist, then reload
  // the run sheet so totals recompute. Mirrors the holiday-override pattern above.
  const saveAdminAdjustments = async (nextMap) => {
    const nextChecklist = { ...checklist, admin_adjustments: nextMap };
    setChecklist(nextChecklist);
    await api.patch(`/admin/submissions/${id}/checklist`, { checklist: nextChecklist });
    await loadRunSheet();
  };

  const addAdminAdjustment = async (employeeId, adjustment) => {
    const map = { ...(checklist.admin_adjustments || {}) };
    const list = Array.isArray(map[employeeId]) ? [...map[employeeId]] : [];
    list.push(adjustment);
    map[employeeId] = list;
    try {
      await saveAdminAdjustments(map);
    } catch (err) {
      console.error('Failed to save admin adjustment:', err);
    }
  };

  const removeAdminAdjustment = async (employeeId, index) => {
    const map = { ...(checklist.admin_adjustments || {}) };
    const list = Array.isArray(map[employeeId]) ? [...map[employeeId]] : [];
    list.splice(index, 1);
    if (list.length === 0) delete map[employeeId];
    else map[employeeId] = list;
    try {
      await saveAdminAdjustments(map);
    } catch (err) {
      console.error('Failed to remove admin adjustment:', err);
    }
  };

  // Payroll notes are per client per calendar month; a submission belongs to the
  // month it was submitted in, matching the admin dashboard's month view.
  const payrollNotesPeriod = submission && submission.submitted_at
    ? { month: new Date(submission.submitted_at).getMonth() + 1, year: new Date(submission.submitted_at).getFullYear() }
    : null;
  const notesCompanyId = submission?.company_id;
  const notesMonth = payrollNotesPeriod?.month;
  const notesYear = payrollNotesPeriod?.year;

  const loadPayrollNotes = useCallback(async () => {
    if (!notesCompanyId || !notesMonth) return;
    try {
      const res = await api.get('/admin/payroll-notes', { params: { month: notesMonth, year: notesYear, company_id: notesCompanyId } });
      setPayrollNotes(res.data || []);
    } catch (err) {
      setPayrollNotes([]);
    }
  }, [notesCompanyId, notesMonth, notesYear]);

  useEffect(() => { loadPayrollNotes(); }, [loadPayrollNotes]);

  const handleAddPayrollNote = async ({ company_id, note }) => {
    await api.post('/admin/payroll-notes', { company_id, note, month: notesMonth, year: notesYear });
    await loadPayrollNotes();
  };

  const handleTogglePayrollNote = async (n) => {
    try {
      await api.patch(`/admin/payroll-notes/${n.id}`, { done: !n.done });
      await loadPayrollNotes();
    } catch (err) {
      setToast({ message: 'Could not update note', type: 'error' });
    }
  };

  const handleDeletePayrollNote = (n) => {
    setConfirmAction({
      title: 'Delete note', message: 'Delete this note?', confirmText: 'Delete', confirmStyle: 'danger',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await api.delete(`/admin/payroll-notes/${n.id}`);
          await loadPayrollNotes();
        } catch (err) {
          setToast({ message: 'Failed to delete note', type: 'error' });
        }
      },
    });
  };

  const load = useCallback(async () => {
    try {
      const subRes = await api.get(`/admin/submissions/${id}`);
      setSubmission(subRes.data);
      const docRes = await api.get(`/documents/company/${subRes.data.company_id}?submission_id=${subRes.data.id}`).catch(() => ({ data: [] }));
      setDocuments(docRes.data || []);
      const saved = subRes.data.processing_checklist;
      if (saved && typeof saved === 'object') setChecklist(saved);
      loadNotes();
      loadActivity();
      loadRunSheet();
      loadQueue(subRes.data);
    } catch (err) {
      setToast({ message: 'Failed to load submission', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, loadNotes, loadActivity, loadRunSheet, loadQueue]);

  useEffect(() => { load(); }, [load]);

  const saveChecklist = useCallback((newChecklist) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/admin/submissions/${id}/checklist`, { checklist: newChecklist });
      } catch (err) {
        console.error('Failed to save checklist:', err);
      }
    }, 500);
  }, [id]);

  const toggleCheck = (key) => {
    setChecklist(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveChecklist(next);
      return next;
    });
  };

  const toggleChangeExpand = (key) => {
    setExpandedChangeKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleProcess = async () => {
    const remaining = totalCount - checkedCount;
    const msg = remaining > 0
      ? `${remaining} of ${totalCount} item${remaining === 1 ? '' : 's'} ha${remaining === 1 ? 's' : 've'} not been marked as entered.\n\nAre you sure you want to mark this submission as processed?`
      : 'Mark this submission as processed?';
    setConfirmAction({
      title: 'Process Submission', message: msg, confirmText: 'Mark as Processed',
      confirmStyle: remaining > 0 ? 'warning' : 'primary',
      onConfirm: async () => {
        setConfirmAction(null);
        setProcessing(true);
        try {
          await api.patch(`/admin/submissions/${id}/process`);
          setSubmission(prev => ({ ...prev, processed: true, processed_at: new Date().toISOString() }));
          setToast({ message: 'Submission marked as processed', type: 'success' });
          loadActivity();
        } catch (err) {
          setToast({ message: 'Failed to process submission', type: 'error' });
        } finally {
          setProcessing(false);
        }
      },
    });
  };

  const handleUnprocess = () => {
    setConfirmAction({
      title: 'Reopen Submission',
      message: 'Reopen this submission? It will be set back to Pending status.',
      confirmText: 'Reopen', confirmStyle: 'warning',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await api.patch(`/admin/submissions/${id}/unprocess`);
          setSubmission(prev => ({ ...prev, processed: false, processed_at: null, submission_status: 'pending' }));
          setToast({ message: 'Submission reopened', type: 'success' });
          loadActivity();
        } catch (err) {
          setToast({ message: 'Failed to reopen submission', type: 'error' });
        }
      },
    });
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.patch(`/admin/submissions/${id}/status`, { status: newStatus });
      setSubmission(prev => ({
        ...prev,
        submission_status: newStatus,
        processed: newStatus === 'processed',
        processed_at: newStatus === 'processed' ? new Date().toISOString() : null,
      }));
      setToast({ message: `Status updated to ${newStatus.replace('_', ' ')}`, type: 'success' });
      loadActivity();
    } catch (err) {
      setToast({ message: 'Failed to update status', type: 'error' });
    }
  };

  const handleDeleteNote = (noteId) => {
    setConfirmAction({
      title: 'Delete Note', message: 'Delete this note?', confirmText: 'Delete', confirmStyle: 'danger',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await api.delete(`/admin/submissions/${id}/notes/${noteId}`);
          setNotes(prev => prev.filter(n => n.id !== noteId));
        } catch (err) {
          setToast({ message: 'Failed to delete note', type: 'error' });
        }
      },
    });
  };

  const handleSaveBrightPay = async (value) => {
    try {
      await api.patch(`/admin/submissions/${id}/brightpay-total`, { brightpay_gross_total: value });
      setRunSheet(prev => prev ? { ...prev, brightpay_gross_total: value, brightpay_total_recorded_at: value !== null ? new Date().toISOString() : null } : prev);
    } catch (err) {
      setToast({ message: 'Failed to save BrightPay total', type: 'error' });
    }
  };

  const handlePdfExport = async () => {
    try {
      const res = await api.get(`/admin/submissions/${submission.id}/pdf`, { responseType: 'blob' });
      if (res.data && res.data.type === 'application/json') {
        const text = await res.data.text();
        let msg = 'Failed to generate PDF';
        try { msg = JSON.parse(text).error || msg; } catch (_) { /* ignore */ }
        setToast({ message: msg, type: 'error' });
        return;
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const filename = `${submission.company_name || 'submission'}-${submission.id.slice(0, 8)}.pdf`;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Failed to download PDF';
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (_) { /* ignore */ }
      } else if (err.response?.data?.error) msg = err.response.data.error;
      else if (err.message) msg = err.message;
      setToast({ message: msg, type: 'error' });
    }
  };

  // ── Loading / not-found states ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bg-[#f6f3fb] min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }
  if (!submission) {
    return (
      <div className="bg-[#f6f3fb] min-h-screen">
        <div className="max-w-4xl mx-auto text-center py-20">
          <p className="text-gray-500">Submission not found.</p>
          <button onClick={() => navigate('/admin')} className="mt-4 text-brand-700 hover:text-brand-800 text-sm font-medium">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Derived data from submission.changes_data ──────────────────────────────
  const data = typeof submission.changes_data === 'string'
    ? JSON.parse(submission.changes_data)
    : submission.changes_data || {};
  const changes = data.changes || [];
  const commissions = (data.commissions || []).filter(c => c.commission > 0);
  const hourlyEntries = (data.hourly_entries || []).filter(h => Number(h.hours_this_period) > 0);

  const ACTION_LIST_EXCLUDED_TYPES = new Set(['addition', 'deduction']);
  const indexedChanges = changes
    .map((change, i) => ({
      ...change,
      checkKey: `change_${i}`,
      employeeName: change.employee_name || `${change.data?.first_name || ''} ${change.data?.last_name || ''}`.trim() || `Item ${i + 1}`,
    }))
    .filter(c => !ACTION_LIST_EXCLUDED_TYPES.has(c.type));

  const grouped = {};
  for (const change of indexedChanges) {
    if (!grouped[change.type]) grouped[change.type] = [];
    grouped[change.type].push(change);
  }

  const sectionsByType = TYPE_ORDER.filter(t => grouped[t]).map(t => ({
    key: `type_${t}`,
    label: TYPE_LABELS[t] || t,
    accent: TYPE_ACCENT[t] || 'brand',
    items: grouped[t],
  }));

  const groupedByEmployee = {};
  for (const change of indexedChanges) {
    const name = change.employeeName;
    if (!groupedByEmployee[name]) groupedByEmployee[name] = [];
    groupedByEmployee[name].push(change);
  }
  const sectionsByEmployee = Object.entries(groupedByEmployee).map(([name, items]) => ({
    key: `emp_${name}`,
    label: name,
    accent: 'brand',
    items,
  }));
  const sections = groupByEmployee ? sectionsByEmployee : sectionsByType;

  const allKeys = indexedChanges.map(c => c.checkKey);
  if (runSheet && Array.isArray(runSheet.rows)) {
    for (const row of runSheet.rows) allKeys.push(`runsheet_${row.employee_id}`);
  }
  const checkedCount = allKeys.filter(k => checklist[k]).length;
  const totalCount = allKeys.length;
  const allChecked = totalCount > 0 && checkedCount === totalCount;

  const submittedDate = new Date(submission.submitted_at);

  // Flag counts for the chip filter row. A row can carry several flags (e.g. a base
  // change AND a bonus), so count each flag rather than one primary per row.
  const flagCounts = {};
  if (runSheet && Array.isArray(runSheet.rows)) {
    for (const row of runSheet.rows) {
      for (const f of deriveFlags(row)) {
        flagCounts[f.kind] = (flagCounts[f.kind] || 0) + 1;
      }
    }
  }

  // Render the rich expansion content for a SummaryTable row.
  const renderRowExpansion = (row) => {
    const hasMidPeriodSplit = row.base_breakdown && row.base_breakdown.hasSplit;
    const grossAdds = (row.adjustments || []).filter(a => a.category === 'gross_add' && a.source !== 'holiday_pay');
    const nonTaxables = (row.adjustments || []).filter(a => a.category === 'non_taxable_add');
    const preTaxItems = (row.adjustments || []).filter(a => a.category === 'pre_tax_deduct');
    const postTaxItems = (row.adjustments || []).filter(a => a.category === 'post_tax_deduct');
    return (
      <div className="text-[14.5px] py-3 pr-5 text-gray-700">
        {/* Base block */}
        <div className="mb-3">
          <div className="flex justify-between font-medium text-ink">
            <span>Base salary{row.base_reduction_total > 0 ? ' (after absence reduction)' : ''}</span>
            <span className="tabular">{formatCurrency(row.base_gross)}</span>
          </div>
          {Array.isArray(row.base_reductions) && row.base_reductions.length > 0 && (
            <div className="mt-1 ml-3 pl-2 border-l-2 border-orange-400 text-orange-800">
              <div className="font-medium mb-0.5">Absence — base reduced</div>
              {row.base_reductions.map((r, i) => (
                <div key={i} className="leading-tight flex justify-between">
                  <span>
                    {r.label}
                    {r.note && <span className="block text-[13px] text-gray-500 italic">{r.note}</span>}
                  </span>
                  {r.amount > 0 && <span className="ml-2 tabular">−{formatCurrency(r.amount)}</span>}
                </div>
              ))}
            </div>
          )}
          {hasMidPeriodSplit && (
            <div className="mt-1 ml-3 pl-2 border-l-2 border-amber-400 text-amber-800">
              <div className="font-medium mb-0.5">Salary changed mid-period</div>
              {row.base_breakdown.segments.map((seg, i) => (
                <div key={i} className="leading-tight">
                  {shortDate(seg.start)}–{shortDate(seg.end)}: {seg.days_worked}d × {formatCurrency(seg.daily_rate)} = {formatCurrency(seg.amount)}
                  <span className="text-gray-500"> ({formatCurrency(seg.annual_salary)}/yr)</span>
                </div>
              ))}
              <div className="text-gray-500 mt-0.5">
                Method: {PRORATA_METHODS.find(m => m.value === (runSheet.pro_rata_method || 'working_260'))?.label}
              </div>
            </div>
          )}
          {row.leaver_partial_breakdown && !hasMidPeriodSplit && (
            <div className="mt-1 ml-3 pl-2 border-l-2 border-rose-400 text-rose-800">
              <div className="font-medium mb-0.5">
                Pro-rated to leaving date {shortDate(row.leaver_partial_breakdown.end)}
              </div>
              <div className="leading-tight">
                {shortDate(row.leaver_partial_breakdown.start)}–{shortDate(row.leaver_partial_breakdown.end)}:{' '}
                {row.leaver_partial_breakdown.days_worked} {row.leaver_partial_breakdown.day_unit} day{row.leaver_partial_breakdown.days_worked === 1 ? '' : 's'}{' '}
                × {formatCurrency(row.leaver_partial_breakdown.daily_rate)} = {formatCurrency(row.leaver_partial_breakdown.amount)}
                <span className="text-gray-500"> ({formatCurrency(row.leaver_partial_breakdown.annual_salary)}/yr)</span>
              </div>
            </div>
          )}
          {row.starter_partial_breakdown && !hasMidPeriodSplit && (() => {
            const sb = row.starter_partial_breakdown;
            const adminMethod = runSheet.pro_rata_method || 'working_260';
            const clientMethod = sb.client_method;
            const methodsDiffer = clientMethod && clientMethod !== adminMethod;
            const clientLabel = PRORATA_METHODS.find(m => m.value === clientMethod)?.label || clientMethod;
            const adminLabel = PRORATA_METHODS.find(m => m.value === adminMethod)?.label || adminMethod;
            return (
              <div className="mt-1 ml-3 pl-2 border-l-2 border-emerald-400 text-emerald-800">
                <div className="font-medium mb-0.5">Pro-rated from start date {shortDate(sb.start)}</div>
                <div className="leading-tight">
                  {shortDate(sb.start)}–{shortDate(sb.end)}: {sb.days_worked} {sb.day_unit} day{sb.days_worked === 1 ? '' : 's'}{' '}
                  × {formatCurrency(sb.daily_rate)} = {formatCurrency(sb.amount)}
                  <span className="text-gray-500"> ({formatCurrency(sb.annual_salary)}/yr)</span>
                </div>
                {clientMethod && (
                  <div className={`mt-0.5 ${methodsDiffer ? 'text-amber-700' : 'text-gray-500'}`}>
                    {methodsDiffer
                      ? <>Client picked <span className="font-medium">{clientLabel}</span>; you're using <span className="font-medium">{adminLabel}</span>.</>
                      : <>Method: {clientLabel} (matches your selection)</>}
                  </div>
                )}
              </div>
            );
          })()}
          {row.hours_breakdown && (
            <div className="mt-1 ml-3 pl-2 border-l-2 border-amber-400 text-amber-800">
              <div className="font-medium mb-0.5">Hourly pay</div>
              <div className="leading-tight">
                {row.hours_breakdown.hours_this_period.toFixed(2)} hrs × {formatCurrency(row.hours_breakdown.hourly_rate)}/hr = {formatCurrency(row.hours_breakdown.gross)}
                {row.hours_breakdown.contracted_hours_per_week && (
                  <span className="text-gray-500"> (contracted {row.hours_breakdown.contracted_hours_per_week} hrs/wk)</span>
                )}
              </div>
              {row.hours_breakdown.mid_period_rate_change && (
                <div className="mt-1 px-2 py-1 rounded bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[13px]">
                  ⚠ Hourly rate changed to {formatCurrency(row.hours_breakdown.mid_period_rate_change.new_rate)}/hr on {shortDate(row.hours_breakdown.mid_period_rate_change.effective_date)}.
                  The figure above uses the snapshot rate ({formatCurrency(row.hours_breakdown.mid_period_rate_change.prev_rate)}/hr) for all hours — split the hours by date and recompute manually if needed.
                </div>
              )}
            </div>
          )}
          {row.prior_period_accrual && (
            <>
              <div className="mt-1 ml-3 pl-2 border-l-2 border-gray-300 text-gray-700 flex items-center justify-between">
                <span>This period{row.period_start && row.period_end ? ` (${shortDate(row.period_start)}–${shortDate(row.period_end)})` : ''}</span>
                <span className="tabular">{formatCurrency(row.period_base_amount)}</span>
              </div>
              <div className="mt-1 ml-3 pl-2 border-l-2 border-emerald-400 text-emerald-800">
                <div className="font-medium mb-0.5 flex items-center justify-between">
                  <span>Prior-month accrual ({shortDate(row.prior_period_accrual.start)}–{shortDate(row.prior_period_accrual.end)})</span>
                  <span className="tabular">{formatCurrency(row.prior_period_accrual.amount)}</span>
                </div>
                <div className="leading-tight">
                  {row.prior_period_accrual.days_worked} working day{row.prior_period_accrual.days_worked === 1 ? '' : 's'} × {formatCurrency(row.prior_period_accrual.daily_rate)} (annual ÷ 260)
                </div>
              </div>
            </>
          )}
          {row.carryover_eligible && !submission.processed && (
            <label className="mt-2 ml-3 inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!excludedCarryover.has(row.employee_id)}
                onChange={() => toggleCarryover(row.employee_id)}
                disabled={runSheetLoading}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-50"
              />
              <span>
                Include prior-month accrual
                <span className="block text-[12px] text-gray-500 normal-case">
                  Untick if these days were already paid through BrightPay last period.
                </span>
              </span>
            </label>
          )}
          {row.previous_base !== null && row.base_changed_from_previous && !hasMidPeriodSplit && !row.leaver_partial_breakdown && (
            <div className="mt-0.5 ml-3 text-amber-700">was {formatCurrency(row.previous_base)} last period</div>
          )}
          {/* Part-time context. The figure above is the pro-rata pay — the number to key
              into BrightPay — so name the pattern it comes from and show the FTE beside
              it. Without this the row gave no way to tell which of the two salaries you
              were looking at. */}
          {row.pt_fraction && (
            <div className="mt-0.5 ml-3 text-gray-500">
              Pro-rata
              {row.pt_weekly_hours && row.pt_fte_weekly_hours
                ? ` — ${row.pt_weekly_hours} of ${row.pt_fte_weekly_hours} hrs`
                : ''}
              {` (${Math.round(row.pt_fraction * 1000) / 10}% of full-time)`}
              {row.fte_annual ? ` · FTE ${formatCurrency(row.fte_annual)}` : ''}
            </div>
          )}
        </div>

        {/* Final pay items the client flagged on the leaver form (bonus / deductions /
            holiday). These are flagged without an amount, so the processor keys the
            figures in via Admin adjustments — surfacing them here means a flagged
            bonus can't be missed when processing a leaver's final pay. */}
        {(row.status_this_period === 'leaver' || row.status_this_period === 'future_leaver')
          && row.client_leaver_data?.final_pay_checklist
          && Object.values(row.client_leaver_data.final_pay_checklist).some(Boolean) && (
          <div className="mb-3 ml-3 pl-2 border-l-2 border-rose-400">
            <div className="font-medium text-rose-800 mb-1">Final pay flagged by client — enter amounts via Admin adjustments below</div>
            <div className="flex flex-wrap gap-1.5">
              {row.client_leaver_data.final_pay_checklist.holiday_pay && (
                <span className="inline-flex items-center rounded-md bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2 py-0.5 text-[13px] font-medium">Holiday pay</span>
              )}
              {row.client_leaver_data.final_pay_checklist.bonus && (
                <span className="inline-flex items-center rounded-md bg-teal-50 text-teal-800 ring-1 ring-teal-300 px-2 py-0.5 text-[13px] font-medium">Bonus</span>
              )}
              {row.client_leaver_data.final_pay_checklist.deductions && (
                <span className="inline-flex items-center rounded-md bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-2 py-0.5 text-[13px] font-medium">Deductions</span>
              )}
            </div>
          </div>
        )}

        {/* Holiday pay */}
        {row.holiday_pay && (
          <div className="mb-3 ml-3 pl-2 border-l-2 border-amber-400">
            <div className="font-medium text-amber-800 mb-1">
              Holiday pay {row.holiday_pay.amount === null && <span className="text-[12px] font-normal italic">— needs admin input</span>}
            </div>
            {row.client_leaver_data && (row.client_leaver_data.holiday_days_owed
              || row.client_leaver_data.holiday_pay_owed
              || row.client_leaver_data.final_payment_adjustments
              || row.client_leaver_data.final_pay_checklist?.holiday_pay) && (
              <div className="text-[13px] text-gray-600 mb-1.5 leading-snug space-y-0.5">
                {(() => {
                  const toCompany = row.client_leaver_data.holiday_balance_direction === 'owed_to_company';
                  return (
                    <>
                      {toCompany && (
                        <div className="font-medium text-rose-700">Holiday overtaken — deduct from final pay</div>
                      )}
                      {row.client_leaver_data.holiday_days_owed && (
                        <div>Client submitted: {row.client_leaver_data.holiday_days_owed} days {toCompany ? 'overtaken' : 'owed'}</div>
                      )}
                      {row.client_leaver_data.holiday_pay_owed && (
                        <div>
                          Client submitted: {toCompany ? '−' : ''}
                          {formatCurrency(parseFloat(row.client_leaver_data.holiday_pay_owed) || 0)}
                          {toCompany ? ' to deduct' : ' owed'}
                        </div>
                      )}
                    </>
                  );
                })()}
                {row.client_leaver_data.final_pay_checklist?.holiday_pay && !row.client_leaver_data.holiday_days_owed && !row.client_leaver_data.holiday_pay_owed && (
                  <div>Client ticked "holiday pay" but didn't supply a figure.</div>
                )}
                {row.client_leaver_data.final_payment_adjustments && <div className="italic">Note: "{row.client_leaver_data.final_payment_adjustments}"</div>}
              </div>
            )}
            <div className="flex items-center gap-2 text-xs">
              <label className="flex items-center gap-1.5">
                <span className="text-gray-700">Holiday days owed:</span>
                <input
                  key={`holiday-${row.employee_id}-${row.holiday_pay.days ?? ''}`}
                  type="number"
                  step="0.5"
                  min="0"
                  defaultValue={row.holiday_pay.days_source === 'admin_override' ? row.holiday_pay.days : ''}
                  placeholder={row.holiday_pay.days_source === 'client_submitted' ? String(row.holiday_pay.days) : '0'}
                  onBlur={(e) => setHolidayDays(row.employee_id, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                  disabled={runSheetLoading || submission.processed}
                  className="w-20 rounded border-gray-300 text-xs py-1 px-2 focus:border-brand-500 focus:ring-brand-500 disabled:opacity-50"
                />
              </label>
              {row.holiday_pay.daily_rate > 0 && (
                <span className="text-gray-500">× {formatCurrency(row.holiday_pay.daily_rate)}/day (annual ÷ 260)</span>
              )}
              {row.holiday_pay.amount !== null && row.holiday_pay.amount > 0 && (
                <span className="ml-auto font-medium text-amber-800">= {formatCurrency(row.holiday_pay.amount)}</span>
              )}
            </div>
            {row.holiday_pay.days_source === 'client_submitted' && (
              <div className="text-[12px] text-gray-500 mt-1">
                Leave blank to use the client's figure ({row.holiday_pay.days} days), or enter a different number to override.
              </div>
            )}
          </div>
        )}

        {(grossAdds.length > 0 || (row.holiday_pay && row.holiday_pay.amount > 0)) && (
          <div className="mb-3">
            {grossAdds.length > 0 && (
              <>
                <div className="text-gray-500 uppercase tracking-wider text-[12px] font-semibold mb-1">Gross additions</div>
                <ul className="space-y-0.5">
                  {grossAdds.map((a, i) => (
                    <li key={i} className="flex justify-between items-baseline">
                      <span className="text-gray-700">
                        {a.recurring && <span className="text-brand-700 mr-1" title="Recurring item">↻</span>}
                        {a.label}
                        {a.kit_metadata && a.kit_metadata.parental_leave_id && (() => {
                          const used = row.kit_days_used?.find(k => k.parental_leave_id === a.kit_metadata.parental_leave_id);
                          return used ? (
                            <span className="ml-1 text-[12px] px-1 py-0.5 rounded bg-pink-100 text-pink-800">{used.days_used}/10 KIT used</span>
                          ) : null;
                        })()}
                        {a.note && <span className="block text-[12px] text-gray-500 italic mt-0.5">{a.note}</span>}
                      </span>
                      <span className="text-ink font-medium tabular">{formatCurrency(a.amount)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="flex justify-between mt-1 pt-1 border-t border-gray-300 font-semibold text-ink">
              <span>Gross pay</span>
              <span className="tabular">{formatCurrency(row.gross_pay)}</span>
            </div>
          </div>
        )}

        {nonTaxables.length > 0 && (
          <div className="mb-3">
            <div className="text-gray-500 uppercase tracking-wider text-[12px] font-semibold mb-1">
              Non-taxable additions <span className="font-normal italic normal-case">(expense reimbursements)</span>
            </div>
            <ul className="space-y-0.5">
              {nonTaxables.map((a, i) => (
                <li key={i} className="flex justify-between">
                  <span className="text-gray-700">
                    {a.label}
                    {a.note && <span className="block text-[12px] text-gray-500 italic mt-0.5">{a.note}</span>}
                  </span>
                  <span className="text-ink font-medium tabular">{formatCurrency(a.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {preTaxItems.length > 0 && (
          <div className="mb-3">
            <div className="text-gray-500 uppercase tracking-wider text-[12px] font-semibold mb-1">
              Pre-tax deductions <span className="font-normal italic normal-case">(reduce taxable pay)</span>
            </div>
            <ul className="space-y-0.5">
              {preTaxItems.map((a, i) => (
                <li key={i} className="flex justify-between">
                  <span className="text-gray-700">
                    {a.recurring && <span className="text-brand-700 mr-1" title="Recurring">↻</span>}
                    {a.source === 'pension_auto' && <span className="text-violet-600 mr-1" title="Auto from pension record">⚙</span>}
                    {a.label}
                    {a.note && <span className="block text-[12px] text-gray-500 italic mt-0.5">{a.note}</span>}
                  </span>
                  <span className="text-rose-600 font-medium tabular">{formatCurrency(a.amount)}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between mt-1 pt-1 border-t border-gray-300 font-semibold text-ink">
              <span>Taxable pay</span>
              <span className="tabular">{formatCurrency(row.taxable_pay)}</span>
            </div>
          </div>
        )}

        {postTaxItems.length > 0 && (
          <div className="mb-2">
            <div className="text-gray-500 uppercase tracking-wider text-[12px] font-semibold mb-1">
              Post-tax deductions <span className="font-normal italic normal-case">(from net pay)</span>
            </div>
            <ul className="space-y-0.5">
              {postTaxItems.map((a, i) => (
                <li key={i} className="flex justify-between">
                  <span className="text-gray-700">
                    {a.recurring && <span className="text-brand-700 mr-1" title="Recurring">↻</span>}
                    {a.source === 'advance_recovery' && <span className="text-amber-600 mr-1" title="Salary advance recovery">↩</span>}
                    {a.source === 'pension_auto' && <span className="text-violet-600 mr-1" title="Auto from pension record">⚙</span>}
                    {a.label}
                    {a.note && <span className="block text-[12px] text-gray-500 italic mt-0.5">{a.note}</span>}
                  </span>
                  <span className="text-rose-600 font-medium tabular">{formatCurrency(a.amount)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(row.status_this_period === 'sick' || row.status_this_period === 'parental_leave') && !grossAdds.some(a => a.source === 'ssp_statutory') && (
          <div className="mt-2 pt-2 border-t border-gray-200 text-gray-500 italic">
            Statutory pay (SMP/SPP/SAP, or SSP under a company-pay tier) computed by BrightPay from absence dates — no figure to type here.
          </div>
        )}

        {row.status_this_period === 'recently_joined' && (
          <div className="mt-2 pt-2 border-t border-gray-200 text-sky-700 italic text-[13px] leading-snug">
            Started {shortDate(row.status_date)} — processed as a new starter last period. BrightPay may show an unexpected figure for their first full month; check the gross pay against {formatCurrency(row.base_gross)} and adjust if needed.
          </div>
        )}

        {row.status_this_period === 'joined_not_processed' && (
          <div className="mt-2 pt-2 border-t border-orange-200 text-orange-800 not-italic text-[13px] leading-snug">
            <span className="font-semibold">Back-pay — needs setting up.</span> Started {shortDate(row.status_date)} but was <span className="font-semibold">not</span> in that period's submission, so they were never processed as a new starter in BrightPay. Set them up now and pay any back-pay owed from their start date; don't assume last month's run already covered them.
          </div>
        )}

        {!hasMidPeriodSplit && grossAdds.length === 0 && preTaxItems.length === 0 && postTaxItems.length === 0 && nonTaxables.length === 0 && row.status_this_period === 'active' && (
          <div className="text-gray-500 italic">No adjustments. Type base salary {formatCurrency(row.base_gross)} into the pay run.</div>
        )}

        <AdminAmendmentList
          amendments={row.admin_amendments}
          onSourceSaved={handleAmendmentSourceSaved}
        />

        <AdminAdjustmentEditor
          employeeId={row.employee_id}
          items={(checklist.admin_adjustments && checklist.admin_adjustments[row.employee_id]) || []}
          dayRate={row.day_rate}
          dayRateBasis={row.day_rate_basis}
          onAdd={addAdminAdjustment}
          onRemove={removeAdminAdjustment}
          formatCurrency={formatCurrency}
        />
      </div>
    );
  };

  // Filter queue per local controls
  const filteredQueue = queue.filter(q => {
    if (queueSearch.trim()) {
      const t = queueSearch.toLowerCase();
      if (!(q.company_name || '').toLowerCase().includes(t)) return false;
    }
    if (queueFilter === 'open') return !q.processed;
    if (queueFilter === 'done') return q.processed;
    return true;
  }).sort((a, b) => {
    // In-progress / pending first, then processed
    if (a.processed !== b.processed) return a.processed ? 1 : -1;
    return new Date(b.submitted_at) - new Date(a.submitted_at);
  });

  const totalGross = runSheet?.totals?.gross_pay || 0;
  const headcount = runSheet?.rows?.length || 0;
  const flagsCount = Object.values(flagCounts).reduce((s, n) => s + n, 0);
  const status = submission.processed ? 'processed' : (submission.submission_status || 'pending');

  return (
    <div className="bg-[#f6f3fb] min-h-screen">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className={`grid grid-cols-1 ${queueCollapsed ? 'xl:grid-cols-[44px_minmax(0,1fr)_360px]' : 'xl:grid-cols-[320px_minmax(0,1fr)_360px]'}`}>
        {/* ── LEFT QUEUE ─────────────────────────────────────── */}
        <aside className="bg-white border-r border-gray-200 hidden xl:block">
          {queueCollapsed ? (
            <button
              onClick={toggleQueueCollapsed}
              title="Expand queue"
              aria-label="Expand queue"
              aria-expanded="false"
              className="w-full h-full flex flex-col items-center gap-3 pt-4 text-gray-500 hover:text-brand-700 hover:bg-gray-50 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              <span className="text-[12px] font-semibold tracking-wider uppercase" style={{ writingMode: 'vertical-rl' }}>
                Queue · {filteredQueue.length}
              </span>
            </button>
          ) : (
          <>
          <div className="px-4 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex items-center justify-between">
              <h3 className="text-[17px] font-bold text-ink">Queue · {submittedDate.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}</h3>
              <button
                onClick={toggleQueueCollapsed}
                title="Collapse queue"
                aria-label="Collapse queue"
                aria-expanded="true"
                className="p-1 -mr-1 rounded text-gray-400 hover:text-brand-700 hover:bg-gray-100 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
            </div>
            <input
              type="text"
              value={queueSearch}
              onChange={(e) => setQueueSearch(e.target.value)}
              placeholder="Search company…"
              className="mt-2.5 w-full rounded-md ring-1 ring-gray-200 border-0 text-[14.5px] py-1.5 px-2.5 focus:ring-brand-500"
            />
            <div className="flex items-center gap-1.5 mt-2.5">
              {[
                { key: 'all', label: 'All', count: queue.length },
                { key: 'open', label: 'Open', count: queue.filter(q => !q.processed).length },
                { key: 'done', label: 'Done', count: queue.filter(q => q.processed).length },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setQueueFilter(t.key)}
                  className={`px-2.5 py-1 text-[14px] font-medium rounded-md transition-colors ${queueFilter === t.key ? 'bg-brand-100 text-brand-700' : 'hover:bg-gray-100 text-gray-600'}`}
                >
                  {t.label} <span className="opacity-60">{t.count}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-y-auto scrollbar-thin" style={{ maxHeight: 'calc(100vh - 180px)' }}>
            {filteredQueue.length === 0 ? (
              <p className="px-4 py-6 text-center text-[14px] text-gray-500">No submissions match.</p>
            ) : filteredQueue.map(s => {
              const active = s.id === submission.id;
              const sStatus = s.processed ? 'processed' : (s.submission_status || 'pending');
              const sData = typeof s.changes_data === 'string' ? JSON.parse(s.changes_data) : s.changes_data || {};
              const sChanges = sData.changes || [];
              const counts = {};
              for (const c of sChanges) {
                const lbl = TYPE_LABELS[c.type] || c.type;
                counts[lbl] = (counts[lbl] || 0) + 1;
              }
              const summary = Object.entries(counts).map(([l, n]) => `${n} ${l}`).join(', ') || 'No changes';
              const isNew = !s.processed && (new Date() - new Date(s.submitted_at)) < 24 * 60 * 60 * 1000;
              const cl = s.processing_checklist || {};
              const rsKeys = Object.keys(cl).filter(k => k.startsWith('runsheet_'));
              const itemsStr = rsKeys.length > 0 ? `${rsKeys.filter(k => cl[k]).length}/${rsKeys.length}` : null;
              return (
                <Link
                  key={s.id}
                  to={`/admin/process/${s.id}`}
                  className={`block px-4 py-3 border-b border-gray-100 transition-colors ${active ? 'bg-brand-50/80' : 'hover:bg-gray-50'}`}
                  style={active ? { borderLeft: '3px solid #ab63f2', paddingLeft: 13 } : { borderLeft: '3px solid transparent', paddingLeft: 13 }}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className={`text-[15.5px] font-semibold truncate ${active ? 'text-brand-900' : 'text-ink'}`}>{s.company_name}</span>
                    {isNew && <span className="px-1.5 py-0.5 bg-brand-600 text-white text-[11px] font-bold rounded uppercase shrink-0">NEW</span>}
                  </div>
                  <p className="text-[14px] text-gray-500 truncate">{summary}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[13px] text-gray-400 tabular">{new Date(s.submitted_at).toLocaleString('en-GB')}</span>
                    <div className="flex items-center gap-2">
                      {itemsStr && <span className="text-[13px] tabular text-gray-600 font-medium">{itemsStr}</span>}
                      <StatusPill status={sStatus} size="sm" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          </>
          )}
        </aside>

        {/* ── MAIN CONTENT ──────────────────────────────────── */}
        <main className="px-4 sm:px-6 lg:px-8 py-6 min-w-0">
          {/* Header */}
          <div className="mb-5">
            <Link to="/admin" className="text-brand-700 text-[15.5px] font-medium inline-flex items-center gap-1 mb-3 hover:text-brand-800">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              Back to Dashboard
            </Link>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <div className="flex items-baseline gap-3 mb-1 flex-wrap">
                  <h1 className="text-[32px] 2xl:text-[38px] text-ink leading-none">{submission.company_name}</h1>
                  <StatusPill status={status} />
                </div>
                <p className="text-gray-500 text-[16px]">
                  Submitted {submittedDate.toLocaleString('en-GB')}
                  {submission.tax_year && (
                    <> · <span className="text-brand-700 font-medium">Tax {submission.tax_year}/{submission.tax_year + 1} M{submission.tax_month}</span></>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handlePdfExport}
                  className="px-3 py-2 rounded-md bg-white ring-1 ring-gray-200 text-[15.5px] font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  PDF
                </button>
                {isTeam && !submission.processed && (
                  <select
                    value={submission.submission_status || 'pending'}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="text-[15.5px] font-medium rounded-md ring-1 ring-gray-200 border-0 py-2 px-2"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="queried">Queried</option>
                  </select>
                )}
                {submission.processed && isTeam && (
                  <button
                    onClick={handleUnprocess}
                    className="px-3 py-2 rounded-md bg-amber-100 ring-1 ring-amber-200 text-amber-800 text-[15.5px] font-medium hover:bg-amber-200"
                  >
                    Reopen
                  </button>
                )}
                {!submission.processed && isTeam && (
                  <button
                    onClick={handleProcess}
                    disabled={processing}
                    className="px-4 py-2 rounded-md bg-brand-600 text-white text-[15.5px] font-semibold hover:bg-brand-700 inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                    {processing ? 'Processing…' : 'Mark Processed'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* KPI Hero */}
          <div className="bg-gradient-to-br from-brand-700 to-brand-900 text-white rounded-2xl p-5 mb-5 shadow-lg grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
            <div className="min-w-0">
              <p className="text-[13.5px] 2xl:text-[14.5px] uppercase tracking-wider opacity-70 font-semibold">Period Gross</p>
              <p className="text-[26px] sm:text-[28px] lg:text-[32px] 2xl:text-[40px] font-bold tabular leading-tight mt-1 break-words">{formatCurrency(totalGross)}</p>
              <p className="text-[13.5px] opacity-70 mt-0.5">Verify against BrightPay</p>
            </div>
            <div className="min-w-0">
              <p className="text-[13.5px] 2xl:text-[14.5px] uppercase tracking-wider opacity-70 font-semibold">Headcount</p>
              <p className="text-[26px] sm:text-[28px] lg:text-[32px] 2xl:text-[40px] font-bold tabular leading-tight mt-1">{headcount}</p>
              <p className="text-[13.5px] opacity-70 mt-0.5">
                {allChecked && totalCount > 0 ? 'all ticked off' : totalCount > 0 ? `${checkedCount} of ${totalCount} entered` : 'no items'}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-[13.5px] 2xl:text-[14.5px] uppercase tracking-wider opacity-70 font-semibold">Flags</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className="text-[26px] sm:text-[28px] lg:text-[32px] 2xl:text-[40px] font-bold tabular leading-tight">{flagsCount}</p>
                <span className="text-[14px] opacity-80">need attention</span>
              </div>
              <p className="text-[13.5px] opacity-70 mt-0.5">
                {[
                  flagCounts.starter && `${flagCounts.starter} starter${flagCounts.starter === 1 ? '' : 's'}`,
                  flagCounts.leaver && `${flagCounts.leaver} leaver${flagCounts.leaver === 1 ? '' : 's'}`,
                  flagCounts.backpay && `${flagCounts.backpay} back-pay`,
                  flagCounts.addition && `${flagCounts.addition} bonus/addition${flagCounts.addition === 1 ? '' : 's'}`,
                  flagCounts.warn && `${flagCounts.warn} pension?`,
                  flagCounts.change && `${flagCounts.change} base change`,
                ].filter(Boolean).join(' · ') || 'none flagged'}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-[13.5px] 2xl:text-[14.5px] uppercase tracking-wider opacity-70 font-semibold">Tax period</p>
              <p className="text-[26px] sm:text-[28px] lg:text-[32px] 2xl:text-[40px] font-bold leading-tight mt-1">
                {submission.tax_year ? `M${submission.tax_month}` : '—'}
              </p>
              <p className="text-[13.5px] opacity-70 mt-0.5">
                {submission.tax_year ? `${submission.tax_year}/${submission.tax_year + 1}` : ''}
              </p>
            </div>
          </div>

          {/* Pro-rata method strip */}
          <div className="bg-white ring-1 ring-gray-200 rounded-xl p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="text-[14.5px] font-semibold text-gray-700 sm:whitespace-nowrap">Pro-rata method:</label>
            <select
              value={proRataMethod}
              onChange={(e) => { const m = e.target.value; setProRataMethod(m); loadRunSheet(m, customDivisor); }}
              disabled={runSheetLoading || submission.processed}
              className="text-[15px] rounded-md ring-1 ring-gray-200 border-0 sm:flex-1 disabled:opacity-60 py-1.5 px-2"
            >
              {PRORATA_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            {proRataMethod === 'custom' && (
              <input
                type="number" min="1"
                value={customDivisor}
                onChange={(e) => setCustomDivisor(e.target.value)}
                onBlur={() => loadRunSheet('custom', customDivisor)}
                disabled={runSheetLoading || submission.processed}
                placeholder="Divisor"
                className="w-24 text-[15px] rounded-md ring-1 ring-gray-200 border-0 py-1.5 px-2 disabled:opacity-60"
              />
            )}
            <p className="text-[14px] text-gray-500 sm:max-w-md leading-snug">
              {PRORATA_METHODS.find(m => m.value === proRataMethod)?.description}
            </p>
          </div>

          {/* Late additions — starters added after this period was submitted. They're
              kept out of the run sheet above (this period is closed) and handled in the
              next open run, so a backdated join can't silently rewrite these figures. */}
          {runSheet?.late_additions && runSheet.late_additions.length > 0 && (
            <div className="bg-orange-50 ring-1 ring-orange-300 rounded-xl p-4 mb-4">
              <p className="text-orange-900 font-semibold text-[15px] flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
                {runSheet.late_additions.length} starter{runSheet.late_additions.length === 1 ? '' : 's'} added after this period was submitted
              </p>
              <p className="text-orange-800 text-[14px] mt-1 leading-snug">
                These were entered after this run was locked, so they're not in the figures below and won't change what you keyed into BrightPay. They'll appear in the next open period's run and are flagged there as back-pay:
              </p>
              <ul className="mt-2 space-y-1">
                {runSheet.late_additions.map(la => (
                  <li key={la.employee_id} className="text-[14px] text-orange-900">
                    <span className="font-medium">{la.employee_name}</span>
                    {la.start_date && <span className="text-orange-700"> — started {shortDate(la.start_date)}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Summary controls */}
          <div className="flex items-end justify-between mb-3 gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="w-1 h-6 bg-brand-500 rounded-full" aria-hidden="true" />
              <h2 className="text-[22px] 2xl:text-[26px] text-ink leading-none">Payroll Entry Summary</h2>
              {runSheet?.rows && <span className="text-gray-400 text-[16px] tabular">({runSheet.rows.length})</span>}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="inline-flex bg-white ring-1 ring-gray-200 rounded-lg p-0.5 text-[14px]">
                {['compact', 'cozy', 'roomy'].map(d => (
                  <button
                    key={d}
                    onClick={() => setDensity(d)}
                    className={`px-3 py-1.5 rounded-md font-medium capitalize ${d === density ? 'bg-brand-100 text-brand-700' : 'text-gray-500 hover:text-gray-700'}`}
                  >{d}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-[14px]">
                <span className="text-gray-500 hidden sm:inline">Rows with flags auto-expand.</span>
                <button onClick={() => setExpandSignal(s => s + 1)} className="text-brand-700 font-medium hover:underline">Expand all</button>
                <span className="text-gray-300">·</span>
                <button onClick={() => setCollapseSignal(s => s + 1)} className="text-brand-700 font-medium hover:underline">Collapse all</button>
              </div>
            </div>
          </div>

          {/* Flag filter chips */}
          {runSheet?.rows && runSheet.rows.length > 0 && (
            <FlagFilterChips
              counts={flagCounts}
              active={flagFilter}
              totalRows={runSheet.rows.length}
              onToggle={(kind) => {
                if (kind === '__clear__') { setFlagFilter(new Set()); return; }
                setFlagFilter(prev => {
                  const n = new Set(prev);
                  if (n.has(kind)) n.delete(kind); else n.add(kind);
                  return n;
                });
              }}
            />
          )}

          {/* Run-sheet states */}
          {runSheetError && (
            <div className="bg-rose-50 ring-1 ring-rose-200 rounded-xl p-3 text-sm text-rose-700 mb-4">
              Couldn't load run sheet: {runSheetError}
            </div>
          )}
          {!runSheet && !runSheetError && (
            <div className="bg-white ring-1 ring-gray-200 rounded-xl p-6 text-center text-sm text-gray-500">
              {runSheetLoading ? 'Loading payroll figures…' : 'No payroll figures yet.'}
            </div>
          )}

          {runSheet && Array.isArray(runSheet.rows) && runSheet.rows.length > 0 && (
            <>
              <SummaryTable
                key={id}
                rows={runSheet.rows}
                total={runSheet.totals?.gross_pay || 0}
                preTaxTotal={runSheet.totals?.pre_tax_deduct || 0}
                postTaxTotal={runSheet.totals?.post_tax_deduct || 0}
                density={density}
                isChecked={(r) => !!checklist[`runsheet_${r.employee_id}`]}
                onToggleCheck={(r) => toggleCheck(`runsheet_${r.employee_id}`)}
                checksDisabled={submission.processed}
                renderExpansion={renderRowExpansion}
                flagFilter={flagFilter}
                expandAllSignal={expandSignal}
                collapseAllSignal={collapseSignal}
              />

              <p className="text-[14px] text-gray-500 mt-3 italic">
                Verify Gross Pay total against BrightPay's payroll summary. Tax + NI computed by BrightPay from taxable pay.
              </p>

              <div className="mt-5">
                <ReconciliationFooter
                  periodTotal={runSheet.totals?.gross_pay || 0}
                  initialBrightPayTotal={runSheet.brightpay_gross_total ?? null}
                  recordedAt={runSheet.brightpay_total_recorded_at}
                  disabled={!isTeam}
                  onSave={handleSaveBrightPay}
                />
              </div>
            </>
          )}

          {/* Documents — compact strip below the table */}
          {documents.length > 0 && (
            <div className="mt-6">
              <SectionLabel count={documents.length}>Uploaded Documents</SectionLabel>
              <div className="bg-white ring-1 ring-gray-200 rounded-xl overflow-hidden">
                <div className="divide-y divide-gray-100">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <span className="text-sm font-medium text-ink">{doc.first_name} {doc.last_name}</span>
                        <span className="text-gray-400 mx-2">·</span>
                        <span className="capitalize text-sm text-gray-600">{(doc.document_type || '').replace(/_/g, ' ')}</span>
                        <span className="text-gray-400 mx-2">·</span>
                        <span className="text-sm text-gray-500">{doc.file_name}</span>
                      </div>
                      <a
                        href={doc.google_drive_url || `/uploads/${doc.file_path}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-brand-700 hover:text-brand-800 text-xs font-medium"
                      >View</a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {submission.processed && (
            <div className="text-center py-4 text-sm text-gray-500">
              Processed {submission.processed_at && `on ${new Date(submission.processed_at).toLocaleString('en-GB')}`}
            </div>
          )}
        </main>

        {/* ── RIGHT CONTEXT RAIL ─────────────────────────── */}
        <aside className="bg-white/60 border-l border-gray-200 backdrop-blur-sm overflow-y-auto scrollbar-thin px-5 py-6 space-y-6 hidden xl:block" style={{ maxHeight: '100vh', position: 'sticky', top: 0 }}>
          {/* Progress */}
          {totalCount > 0 && (
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <h4 className="text-[14.5px] font-bold uppercase tracking-[0.12em] text-ink">Processing</h4>
                <span className="text-[15.5px] tabular text-gray-600">
                  <span className={`font-bold ${allChecked ? 'text-emerald-600' : 'text-brand-700'}`}>{checkedCount}</span> / {totalCount}
                </span>
              </div>
              <ProgressBar done={checkedCount} total={totalCount} height={8} />
              {allChecked && !submission.processed && (
                <p className="text-[13.5px] text-emerald-700 mt-2 font-medium">All entered — ready to mark processed.</p>
              )}
            </div>
          )}

          {/* Nil return */}
          {submission.submission_type === 'no_changes' && changes.length === 0 && commissions.length === 0 && hourlyEntries.length === 0 && (
            <div className="bg-sky-50 ring-1 ring-sky-200 rounded-xl p-4">
              <p className="text-sky-800 font-semibold text-[15.5px]">Nil Return</p>
              <p className="text-sky-700 text-[14.5px] mt-1">No payroll changes for this period.</p>
            </div>
          )}

          {/* Changes (Action List restyled into right rail) */}
          {indexedChanges.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-[14.5px] font-bold uppercase tracking-[0.12em] text-ink">Changes <span className="text-gray-400 tabular">({indexedChanges.length})</span></h4>
                {changes.length > 1 && (
                  <div className="inline-flex bg-gray-100 rounded-md p-0.5 text-[13.5px]">
                    <button
                      onClick={() => setGroupByEmployee(true)}
                      className={`px-2 py-0.5 rounded ${groupByEmployee ? 'bg-white shadow-sm font-medium text-brand-700' : 'text-gray-500'}`}
                    >Emp.</button>
                    <button
                      onClick={() => setGroupByEmployee(false)}
                      className={`px-2 py-0.5 rounded ${!groupByEmployee ? 'bg-white shadow-sm font-medium text-brand-700' : 'text-gray-500'}`}
                    >Type</button>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {sections.map(section => (
                  <div key={section.key}>
                    <p className="text-[12.5px] font-bold tracking-[0.1em] uppercase text-ink/70 mb-1.5">
                      {section.label} <span className="text-gray-400 font-normal">({section.items.length})</span>
                    </p>
                    <div className="space-y-2">
                      {section.items.map(change => {
                        const isChecked = !!checklist[change.checkKey];
                        const isExpanded = expandedChangeKeys.has(change.checkKey);
                        const fields = getFields(change).filter(f => f.value !== undefined && f.value !== null && f.value !== '');
                        const summary = getOneLineSummary(change);
                        const accent = TYPE_ACCENT[change.type] || 'emerald';
                        const accentRing = { emerald: 'ring-emerald-200 bg-emerald-50/60', rose: 'ring-rose-200 bg-rose-50/60', amber: 'ring-amber-200 bg-amber-50/60', sky: 'ring-sky-200 bg-sky-50/60', brand: 'ring-brand-200 bg-brand-50/60' }[accent];
                        const accentText = { emerald: 'text-emerald-900', rose: 'text-rose-900', amber: 'text-amber-900', sky: 'text-sky-900', brand: 'text-brand-900' }[accent];
                        const accentDot = { emerald: 'bg-emerald-500', rose: 'bg-rose-500', amber: 'bg-amber-500', sky: 'bg-sky-500', brand: 'bg-brand-500' }[accent];
                        return (
                          <div key={change.checkKey} className={`rounded-lg ring-1 ${accentRing} overflow-hidden ${isChecked ? 'opacity-70' : ''}`}>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => toggleChangeExpand(change.checkKey)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleChangeExpand(change.checkKey); } }}
                              className="w-full text-left px-2.5 py-2 hover:bg-white/30 transition-colors cursor-pointer"
                            >
                              <div className="flex items-center justify-between gap-2 mb-0.5">
                                <span className={`text-[14.5px] font-semibold ${accentText} truncate`}>
                                  {groupByEmployee ? (TYPE_LABELS[change.type] || change.type) : change.employeeName}
                                </span>
                                {submission.processed ? (
                                  <span className={`inline-flex items-center gap-1 text-[12.5px] font-semibold ${isChecked ? accentText : 'text-gray-500'} flex-shrink-0`}>
                                    {isChecked && <span className={`w-1.5 h-1.5 rounded-full ${accentDot}`} />}
                                    {isChecked ? 'Entered' : 'Not entered'}
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleCheck(change.checkKey); }}
                                    className={`px-2 py-0.5 rounded text-[12.5px] font-semibold flex-shrink-0 ${isChecked ? 'bg-white text-emerald-700 ring-1 ring-emerald-200' : 'bg-white/70 text-gray-600 ring-1 ring-gray-300 hover:ring-brand-300'}`}
                                  >
                                    {isChecked ? '✓ Entered' : 'Mark entered'}
                                  </button>
                                )}
                              </div>
                              {summary && <p className="text-[13.5px] text-gray-600 truncate">{summary}</p>}
                            </div>
                            {isExpanded && fields.length > 0 && (
                              <div className="divide-y divide-white/40 bg-white/40 border-t border-white/40">
                                {fields.map((f, j) => <CopyField key={j} label={f.label} value={f.value} copyValue={f.copyValue} warn={f.warn} />)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Commissions */}
          {commissions.length > 0 && (
            <div>
              <h4 className="text-[14.5px] font-bold uppercase tracking-[0.12em] text-ink mb-2">Commissions <span className="text-gray-400 tabular">({commissions.length})</span></h4>
              <div className="rounded-lg ring-1 ring-violet-200 bg-violet-50/60 p-3">
                {commissions.map((c, i) => (
                  <div key={i} className="flex justify-between text-[15px] mb-1 last:mb-0">
                    <span className="font-semibold text-violet-900 truncate mr-2">{c.employee_name || `Employee ${i + 1}`}</span>
                    <span className="tabular font-semibold text-violet-900 shrink-0">{formatCurrency(c.commission)}</span>
                  </div>
                ))}
                <div className="mt-2 pt-2 border-t border-violet-200 flex justify-between text-[14.5px]">
                  <span className="text-violet-700">Total</span>
                  <span className="tabular font-bold text-violet-900">{formatCurrency(commissions.reduce((s, c) => s + (Number(c.commission) || 0), 0))}</span>
                </div>
              </div>
            </div>
          )}

          {/* Payroll notes — the client's month, shared with the dashboard's Notes tab */}
          {payrollNotesPeriod && (
            <div>
              <h4 className="text-[14.5px] font-bold uppercase tracking-[0.12em] text-ink mb-2">
                Payroll notes · {MONTH_NAMES[payrollNotesPeriod.month - 1]}{' '}
                <span className="text-gray-400 tabular">({payrollNotes.filter(n => !n.done).length} open)</span>
              </h4>
              <PayrollNotes
                compact
                notes={payrollNotes}
                month={payrollNotesPeriod.month}
                year={payrollNotesPeriod.year}
                companyId={submission.company_id}
                canDelete={(n) => isAdmin || n.created_by === user?.id}
                onAdd={handleAddPayrollNote}
                onToggle={handleTogglePayrollNote}
                onDelete={handleDeletePayrollNote}
              />
            </div>
          )}

          {/* Earlier per-submission notes, from before payroll notes existed. Read-only. */}
          {notes.length > 0 && (
          <div>
            <h4 className="text-[14.5px] font-bold uppercase tracking-[0.12em] text-ink mb-2">Earlier notes <span className="text-gray-400 tabular">({notes.length})</span></h4>
            {notes.length > 0 && (
              <ol className="mt-3 space-y-2">
                {notes.map(note => (
                  <li key={note.id} className="bg-white/70 ring-1 ring-gray-200 rounded-md p-2.5 text-[14px]">
                    <p className="text-ink whitespace-pre-wrap">{note.note}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[13px] text-gray-500">
                        {note.author_name} — {new Date(note.created_at).toLocaleString('en-GB')}
                      </span>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-gray-300 hover:text-rose-500"
                          title="Delete note"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
          )}

          {/* Activity */}
          <div>
            <h4 className="text-[14.5px] font-bold uppercase tracking-[0.12em] text-ink mb-2">Activity</h4>
            {activity.length === 0 ? (
              <p className="text-[14px] text-gray-500 italic">Activity will appear as this submission is processed.</p>
            ) : (
              <ol className="space-y-2.5 text-[14px]">
                {activity.map(a => {
                  const dotColor = a.action.startsWith('submission_status_processed')
                    ? 'bg-emerald-500'
                    : a.action === 'submission_brightpay_total_set'
                    ? 'bg-sky-500'
                    : a.action.startsWith('submission_status')
                    ? 'bg-brand-500'
                    : a.action === 'submission_submitted' || a.action === 'submission_created'
                    ? 'bg-emerald-500'
                    : 'bg-gray-400';
                  const who = a.first_name ? `${a.first_name} ${a.last_name || ''}`.trim() : (a.email || 'System');
                  const label = ({
                    submission_status_processed: 'marked Processed',
                    submission_status_pending: 'reopened as Pending',
                    submission_status_in_progress: 'marked In Progress',
                    submission_status_queried: 'flagged Queried',
                    submission_brightpay_total_set: 'recorded the BrightPay total',
                    submission_created: 'created the submission',
                    submission_submitted: 'submitted changes',
                  })[a.action] || a.action.replace(/_/g, ' ');
                  return (
                    <li key={a.id} className="flex gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${dotColor} mt-1.5 shrink-0`} aria-hidden="true" />
                      <div className="min-w-0">
                        <p className="text-gray-700"><span className="font-semibold text-ink">{who}</span> {label}</p>
                        <p className="text-gray-400">{new Date(a.created_at).toLocaleString('en-GB')}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </aside>
      </div>

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          confirmStyle={confirmAction.confirmStyle}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
